import React, { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import styles from './LinearLoader.module.scss';
import snakeImg from '../../assets/images/snake.png';
import snakeImg2x from '../../assets/images/snake@2x.png';

const DEFAULT_DURATION = 5000;
const MAX_PROGRESS = 100;
const WAIT_PROGRESS = 99;

export interface Props {
  isLoading: boolean;
  duration?: number; // in milliseconds
  color: 'primary' | 'gradient';
  hasSnail?: boolean;
  className?: string;
  onSuccess?: () => void;
}

const LinearLoader: React.FC<Props> = ({
  isLoading,
  duration = 0,
  color = 'primary',
  hasSnail = false,
  className,
  onSuccess,
}) => {
  const [isVisible, setIsVisible] = useState(isLoading);
  const [progress, setProgress] = useState(0);
  const durationTotal = useMemo(() => DEFAULT_DURATION + duration, [duration]);
  const intervalSpeed = useMemo(
    () => durationTotal / MAX_PROGRESS,
    [durationTotal]
  );
  const isWaiting = isLoading && progress >= WAIT_PROGRESS;

  useEffect(() => {
    let progressInterval: NodeJS.Timeout;

    if (isLoading && !isVisible) {
      setIsVisible(true);
      setProgress(0);
    }

    if (isWaiting && isVisible && !isLoading) {
      setProgress(MAX_PROGRESS);
      setIsVisible(false);
    }

    if (isVisible && !isWaiting) {
      progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (isLoading && prev >= WAIT_PROGRESS) {
            return WAIT_PROGRESS;
          }
          if (prev >= MAX_PROGRESS && !isLoading) {
            clearInterval(progressInterval);
            setIsVisible(false);
            return MAX_PROGRESS;
          }
          if (isLoading && prev >= MAX_PROGRESS) {
            return MAX_PROGRESS;
          }
          return prev + 1;
        });
      }, intervalSpeed);
    }

    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
    };
  }, [isLoading, isVisible, intervalSpeed, isWaiting]);

  useEffect(() => {
    if (!isVisible && progress >= MAX_PROGRESS && onSuccess) {
      onSuccess();
      setProgress(0);
    }
  }, [isVisible, progress, onSuccess]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={classNames(
        styles.linearLoader,
        { [styles.paddingTop]: hasSnail },
        'd-flex flex-ai-c gap-xxxs',
        className
      )}>
      <div className={styles.progressBarWrapper}>
        <div className={styles.progressTrack}>
          <div
            className={classNames(styles.progressFill, styles[color])}
            style={{
              width: `${progress}%`,
              transitionDuration: `${intervalSpeed}ms`,
            }}
          />
          {isWaiting && <div className={styles.blinkingStripe} />}
        </div>
        {hasSnail && (
          <img
            className={styles.snail}
            style={{
              left: `${progress}%`,
              transitionDuration: `${intervalSpeed}ms`,
            }}
            src={snakeImg}
            alt='Snake'
            srcSet={`${snakeImg2x} 2x`}
          />
        )}
      </div>
      <span className={classNames(styles.progressText, 'text-sm fw-medium')}>
        {Math.round(progress)}%
      </span>
    </div>
  );
};

export default LinearLoader;
